import { memo, useCallback, useMemo } from 'react';

import { useTrackAgentPillClicked } from '@air/analytics';

import { WorkspaceAgentAssetListPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentAssetListPill';
import { WorkspaceAgentPendingPill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentPendingPill';
import { WorkspaceAgentUnavailablePill } from '~/components/v2/components/workspace/workspace-agent-panel/components/shared/WorkspaceAgentUnavailablePill';
import { useOpenWorkspaceAgentAssetPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useOpenWorkspaceAgentAssetPill';
import { usePrivateWorkspaceAgentAssetListThumbnails } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentAssetListThumbnails';
import { usePrivateWorkspaceAgentHydratedArtifactPill } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/usePrivateWorkspaceAgentHydratedArtifactPill';
import { useRegisterWorkspaceAgentAssetPillClipIds } from '~/components/v2/components/workspace/workspace-agent-panel/hooks/useRegisterWorkspaceAgentAssetPillClipIds';
import { type AssetListPill } from '~/components/v2/components/workspace/workspace-agent-panel/schemas/pillMetadata';
import { flattenWorkspaceAgentAssetPillItems } from '~/components/v2/components/workspace/workspace-agent-panel/utils/flattenWorkspaceAgentAssetPillItems';

export type PrivateWorkspaceAgentAssetListPillChipProps = {
  isStreaming: boolean;
  pill: AssetListPill;
};

export const PrivateWorkspaceAgentAssetListPillChip = memo(
  ({ isStreaming, pill }: PrivateWorkspaceAgentAssetListPillChipProps) => {
    const {
      inViewRef,
      isPending,
      isUnavailable,
      pill: hydrated,
    } = usePrivateWorkspaceAgentHydratedArtifactPill({
      isStreaming,
      pill,
    });
    const listPill = hydrated.type === 'assetList' ? hydrated : pill;
    const listCount = listPill.count;
    const displayPills = useMemo(() => [listPill], [listPill]);
    const assetItems = useMemo(() => flattenWorkspaceAgentAssetPillItems(displayPills), [displayPills]);

    useRegisterWorkspaceAgentAssetPillClipIds(displayPills);

    const { thumbnails, unavailableCount } = usePrivateWorkspaceAgentAssetListThumbnails(assetItems, { isStreaming });
    const { openAssetClip } = useOpenWorkspaceAgentAssetPill();
    const { trackAgentPillClicked } = useTrackAgentPillClicked();

    const handleOpenThumbnail = useCallback(
      (id: string) => {
        if (!assetItems.some((item) => item.clipId === id)) {
          return;
        }

        const thumbnail = thumbnails.find((entry) => entry.id === id);
        trackAgentPillClicked({
          object: 'asset_list',
          object_count: assetItems.length,
          object_name: thumbnail?.label ?? 'Selected assets',
        });
        openAssetClip(id);
      },
      [assetItems, openAssetClip, thumbnails, trackAgentPillClicked],
    );

    const canOpenThumbnails = thumbnails.some((thumbnail) => thumbnail.canOpen);

    if (isPending) {
      return (
        <span className="inline-flex max-w-full items-center align-middle leading-none" ref={inViewRef}>
          <WorkspaceAgentPendingPill label="Assets" />
        </span>
      );
    }

    if (isUnavailable) {
      return (
        <span className="inline-flex max-w-full items-center align-middle leading-none" ref={inViewRef}>
          <WorkspaceAgentUnavailablePill count={listCount} kind="asset" referenceId={listPill.id} />
        </span>
      );
    }

    // Members were named and every one settled missing. A count-only handle has no members to lose.
    if (thumbnails.length > 0 && unavailableCount === thumbnails.length) {
      return <WorkspaceAgentUnavailablePill count={listCount} kind="asset" referenceId={listPill.id} />;
    }

    return (
      <WorkspaceAgentAssetListPill
        count={listCount}
        onOpenThumbnail={canOpenThumbnails ? handleOpenThumbnail : undefined}
        thumbnails={thumbnails}
        unavailableCount={unavailableCount}
      />
    );
  },
);

PrivateWorkspaceAgentAssetListPillChip.displayName = 'PrivateWorkspaceAgentAssetListPillChip';
